import {
  Component,
  OnInit,
  OnDestroy,
  NgZone,
  ChangeDetectorRef,
  HostListener,
  ElementRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Track, RoomEvent } from 'livekit-client';
import { VideoCallService } from '../../services/video-call.service';

@Component({
  selector: 'app-pip-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pip-overlay.component.html',
  styleUrls: ['./pip-overlay.component.scss'],
})
export class PipOverlayComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('remoteVideo') remoteVideoEl?: ElementRef<HTMLVideoElement>;
  @ViewChild('localVideo') localVideoEl?: ElementRef<HTMLVideoElement>;
  @ViewChild('pipStrip') pipStripEl?: ElementRef<HTMLElement>;

  posX = 16;
  posY = 0;

  private dragging = false;
  private offsetX = 0;
  private offsetY = 0;
  private refreshInterval?: ReturnType<typeof setInterval>;
  private wasMinimized = false;

  constructor(
    public callService: VideoCallService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.posY = window.innerHeight - 130;
    this.refreshInterval = setInterval(() => {
      if (this.callService.isMinimized) {
        if (!this.wasMinimized) {
          this.wasMinimized = true;
          setTimeout(() => this.attachTracks(), 200);
        }
        this.cdr.detectChanges();
      } else {
        this.wasMinimized = false;
      }
    }, 500);
  }

  ngAfterViewInit(): void {
    if (this.callService.isMinimized) {
      this.attachTracks();
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
    this.detachTracks();
  }

  private attachTracks(): void {
    if (!this.callService.room) return;

    if (this.remoteVideoEl) {
      const remote = Array.from(
        this.callService.room.remoteParticipants.values(),
      )[0];
      if (remote) {
        const camPub = remote.getTrackPublication(Track.Source.Camera);
        if (camPub?.track) {
          camPub.track.attach(this.remoteVideoEl.nativeElement);
        }
      }
    }

    if (this.localVideoEl) {
      const local = this.callService.room.localParticipant;
      const camPub = local.getTrackPublication(Track.Source.Camera);
      if (camPub?.track) {
        camPub.track.attach(this.localVideoEl.nativeElement);
      }
    }
  }

  private detachTracks(): void {
    if (!this.callService.room) return;

    if (this.remoteVideoEl) {
      const remote = Array.from(
        this.callService.room.remoteParticipants.values(),
      )[0];
      if (remote) {
        const camPub = remote.getTrackPublication(Track.Source.Camera);
        if (camPub?.track) {
          camPub.track.detach(this.remoteVideoEl.nativeElement);
        }
      }
    }

    if (this.localVideoEl) {
      const local = this.callService.room.localParticipant;
      const camPub = local.getTrackPublication(Track.Source.Camera);
      if (camPub?.track) {
        camPub.track.detach(this.localVideoEl.nativeElement);
      }
    }
  }

  get remoteParticipantName(): string {
    if (!this.callService.room) return '';
    const remote = Array.from(
      this.callService.room.remoteParticipants.values(),
    )[0];
    return remote?.name || remote?.identity || 'Doctor';
  }

  get remoteParticipantInitial(): string {
    return this.remoteParticipantName.charAt(0).toUpperCase();
  }

  get hasRemoteVideo(): boolean {
    if (!this.callService.room) return false;
    const remote = Array.from(
      this.callService.room.remoteParticipants.values(),
    )[0];
    if (!remote) return false;
    const cam = remote.getTrackPublication(Track.Source.Camera);
    return !!cam && !cam.isMuted && !!cam.track;
  }

  get isMicEnabled(): boolean {
    const mic = this.callService.room?.localParticipant?.getTrackPublication(
      Track.Source.Microphone,
    );
    return !!mic && !mic.isMuted;
  }

  get isCameraEnabled(): boolean {
    const cam = this.callService.room?.localParticipant?.getTrackPublication(
      Track.Source.Camera,
    );
    return !!cam && !cam.isMuted;
  }

  async toggleMic(): Promise<void> {
    if (!this.callService.room) return;
    await this.callService.room.localParticipant.setMicrophoneEnabled(
      !this.isMicEnabled,
    );
  }

  async toggleCamera(): Promise<void> {
    if (!this.callService.room) return;
    await this.callService.room.localParticipant.setCameraEnabled(
      !this.isCameraEnabled,
    );
    setTimeout(() => this.attachTracks(), 300);
  }

  restore(): void {
    this.detachTracks();
    const roomName = this.callService.roomName;
    const callType = this.callService.callType;
    this.callService.restore();
    this.router.navigate([callType === 'audio' ? '/patients/audio-room' : '/patients/video-room', roomName]);
  }

  leave(): void {
    this.detachTracks();
    this.callService.room?.disconnect();
    this.callService.reset();
  }

  onDragStart(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('.pip-controls')) return;
    this.dragging = true;
    this.offsetX = event.clientX - this.posX;
    this.offsetY = event.clientY - this.posY;
    event.preventDefault();
  }

  onTouchStart(event: TouchEvent): void {
    if ((event.target as HTMLElement).closest('.pip-controls')) return;
    const touch = event.touches[0];
    this.dragging = true;
    this.offsetX = touch.clientX - this.posX;
    this.offsetY = touch.clientY - this.posY;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.dragging) return;
    this.posX = event.clientX - this.offsetX;
    this.posY = event.clientY - this.offsetY;
    this.clamp();
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.dragging) return;
    const touch = event.touches[0];
    this.posX = touch.clientX - this.offsetX;
    this.posY = touch.clientY - this.offsetY;
    this.clamp();
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDragEnd(): void {
    this.dragging = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Keep the strip fully on-screen after an orientation change / viewport resize.
    this.clamp();
  }

  private clamp(): void {
    const w = this.pipStripEl?.nativeElement?.offsetWidth || 292;
    const h = this.pipStripEl?.nativeElement?.offsetHeight || 120;
    this.posX = Math.max(0, Math.min(this.posX, window.innerWidth - w));
    this.posY = Math.max(0, Math.min(this.posY, window.innerHeight - h));
  }
}
